/* eslint-disable @typescript-eslint/no-explicit-any */
import { api_service } from '../api/api-service';
import { api_urls } from '../api/api-urls';

/** Raw item from API data.paymentMethods (cards and us_bank_account). */
export interface StripePaymentMethodApiItem {
    id: string;
    type: 'card' | 'us_bank_account';
    customerId?: string;
    created?: number;
    last4: string;
    bankName?: string;
    accountHolderName?: string;
    accountType?: string;
    brand?: string;
    expMonth?: number;
    expYear?: number;
    country?: string;
    funding?: string;
}

/** Shape used in app for displaying a saved payment method (card or bank). */
export interface StripePaymentMethodItem {
    id: string;
    label: string;
    last4?: string;
    type?: 'card' | 'us_bank_account';
    brand?: string;
    expiryMonth?: number;
    expiryYear?: number;
    bankName?: string;
    accountHolderName?: string;
    accountType?: string;
    country?: string;
    funding?: string;
}

function mapPaymentMethodApiToDisplay(raw: StripePaymentMethodApiItem): StripePaymentMethodItem {
    const last4 = raw.last4 ?? '';
    if (raw.type === 'us_bank_account') {
        const bankName = raw.bankName?.trim() || 'Bank account';
        return {
            id: raw.id,
            type: 'us_bank_account',
            label: `${bankName} ****${last4}`,
            last4,
            bankName: raw.bankName,
            accountHolderName: raw.accountHolderName,
            accountType: raw.accountType,
        };
    }
    const brand = (raw.brand && raw.brand.charAt(0).toUpperCase() + raw.brand.slice(1)) || 'Card';
    return {
        id: raw.id,
        type: 'card',
        label: `${brand} ****${last4}`,
        last4,
        brand: raw.brand,
        expiryMonth: raw.expMonth,
        expiryYear: raw.expYear,
        country: raw.country,
        funding: raw.funding,
    };
}

/** One PaymentIntent from backend (e.g. create payment-intents response). */
export interface StripePaymentIntentItem {
    id: string;
    clientSecret: string;
    amount: number;
    metadata?: { contractId?: string; paymentId?: string; [key: string]: string | undefined };
}

export interface PaymentApiResponse {
    Id: string;
    UserId?: string;
    ContractId: string;
    AspireContractId: string;
    InvoiceOId: string;
    DueDate: string | null;
    PaymentDate: string | null;
    Amount: string | number;
    TransactionCode: string | null;
    PaymentType: string | null;
    ReferenceNumber: string | null;
    Platform: number | null;
    CreatedTime: string;
    UpdatedTime: string;
    otherAmount?: number;
    sub?: PaymentApiResponse[];
}

export interface PaymentHistoryResponse {
    statusCode: number;
    data: PaymentApiResponse[];
    totalCount?: number;
    message?: string;
}

export interface PaymentInvoiceDetail {
    invoiceHeaderOId: string;
    appliedAmount: number;
    description: string;
    transactionCode: string;
    dueDate: string;
    paymentType: string;
    status: string;
    platform?: number;
}

export interface PaymentHistoryItemNew {
    referenceNumber: string;
    amount: string;
    paymentDate: string;
    paymentType: string;
    contractId: string;
    platform?: number;
    invoices: PaymentInvoiceDetail[];
}

export interface PaymentHistoryResponseNew {
    statusCode: number;
    data: PaymentHistoryItemNew[];
    message?: string;
}

/**
 * Get payment history for a contract
 * @param contractId - Contract ID (database Id, not AspireId)
 * @param showLoader - Whether to show loading indicator (default: true)
 * @returns Promise with payment history response (new format with invoices)
 */
export const getPaymentHistory = async (
    contractId: string | null | undefined | number,
    showLoader: boolean = true,
): Promise<PaymentHistoryResponseNew> => {
    try {
        const response: any = await api_service.get({
            endPoint: `${api_urls.GetPaymentHistory}/${contractId}`,
            showLoader,
        });
        const responseData = response?.data || response;

        if (responseData.statusCode && Array.isArray(responseData.data)) {
            return responseData as PaymentHistoryResponseNew;
        } else if (Array.isArray(responseData)) {
            return { statusCode: 200, data: responseData };
        }
        return { statusCode: 200, data: [] };
    } catch (error: any) {
        console.error('Error fetching payment history:', error);
        throw error;
    }
};

export interface CreatePaymentPayload {
    contractId: string;
    amount: number;
    paymentDate: string; // ISO format: "2025-11-30T00:00:00.000Z"
    bankAccountId: string;
}

export interface CreatePaymentResponse {
    statusCode: number;
    data?: any;
    message?: string;
}

/**
 * Create a payment
 * @param payload - Payment payload
 * @param showLoader - Whether to show loading indicator (default: true)
 * @returns Promise with create payment response
 */
export const makePayment = async (
    payload: CreatePaymentPayload,
    showLoader: boolean = true,
): Promise<CreatePaymentResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.MakePayment,
            payLoad: payload,
            showLoader,
        });
        return response?.data as CreatePaymentResponse;
    } catch (error: any) {
        console.error('Error creating payment:', error);
        throw error;
    }
};

/**
 * Get user-based payment history (all payments for the logged-in user)
 * @param showLoader - Whether to show loading indicator (default: true)
 * @returns Promise with payment history response (new format with invoices)
 */
export const getUserBasedPaymentHistory = async (showLoader: boolean = true): Promise<PaymentHistoryResponseNew> => {
    try {
        const response: any = await api_service.get({
            endPoint: api_urls.GetUserBasedPaymentHistory,
            showLoader,
        });
        // Handle axios response structure
        const responseData = response?.data || response;

        // If responseData has statusCode and data, return it
        if (responseData.statusCode && Array.isArray(responseData.data)) {
            return responseData as PaymentHistoryResponseNew;
        } else if (Array.isArray(responseData)) {
            // If responseData is directly an array, wrap it
            return { statusCode: 200, data: responseData };
        }
        return { statusCode: 200, data: [] };
    } catch (error: any) {
        console.error('Error fetching user-based payment history:', error);
        throw error;
    }
};

export interface OpenInvoiceApiResponse {
    Id: string;
    ContractId: string;
    contractId?: string;
    AspireContractId: string;
    InvoiceNumber: string;
    DueDate: string;
    Amount: string;
    IsActive: boolean;
    Platform: number;
    CreatedTime: string;
    UpdatedTime: string;
    duedate?: string;
    aspireContractId?: string;
    amount?: string;
    invoiceNumber?: string;
    is_scheduled?: boolean;
    IsScheduled?: boolean;
    segregation?: Array<{
        Description: string;
        Amount: string;
    }>;
}

export interface OpenInvoicesResponse {
    statusCode: number;
    data: OpenInvoiceApiResponse[];
    message?: string;
    pendingScheduleAmount?: Array<{
        contractId: string;
        totalPendingScheduleAmount: number;
    }>;
}

/**
 * Get open invoices (upcoming payments) for the logged-in user
 * @param showLoader - Whether to show loading indicator (default: true)
 * @returns Promise with open invoices response
 */
export const getOpenInvoices = async (showLoader: boolean = true): Promise<OpenInvoicesResponse> => {
    try {
        const response: any = await api_service.get({
            endPoint: api_urls.UpcomingPayments,
            showLoader,
        });
        // Handle axios response structure
        const responseData = response?.data || response;

        // If responseData has statusCode and invoices array, map to data field
        if (responseData?.statusCode && responseData?.data?.invoices) {
            return {
                statusCode: responseData?.statusCode,
                message: responseData?.message,
                data: responseData?.data?.invoices,
                pendingScheduleAmount: responseData?.data?.pendingScheduleAmount,
            };
        } else if (Array.isArray(responseData)) {
            // If responseData is directly an array, wrap it
            return { statusCode: 200, data: responseData };
        }
        return { statusCode: 200, data: [] };
    } catch (error: any) {
        console.error('Error fetching open invoices:', error);
        throw error;
    }
};

export const getScheduledPayments = async (showLoader: boolean = true): Promise<OpenInvoicesResponse> => {
    try {
        const response: any = await api_service.get({
            endPoint: api_urls.GetScheduledPayments,
            showLoader,
        });
        // Handle axios response structure
        const responseData = response?.data || response;
        // If responseData has statusCode and data, return it
        if (responseData.statusCode && Array.isArray(responseData.data)) {
            return responseData;
        } else if (Array.isArray(responseData)) {
            // If responseData is directly an array, wrap it
            return { statusCode: 200, data: responseData };
        }
        return { statusCode: 200, data: [] };
    } catch (error: any) {
        console.error('Error fetching open invoices:', error);
        throw error;
    }
};

export interface GetStripePaymentMethodsResponse {
    statusCode: number;
    data: StripePaymentMethodItem[];
    message?: string;
}

/** Fee entry returned by GET /payments/fees for a country/state combination. */
export interface FeeResponse {
    country: string;
    state: string;
    charge: string | null;
    fees: string | null;
    isActive?: boolean;
}

/**
 * Fetch card processing fees by country/state.
 * GET /payments/fees — called when the user selects the Card payment method.
 */
export const getPaymentFees = async (
    showLoader: boolean = false,
    userContractsCountryCombinations?: { country: string; state: string }[],
): Promise<{ statusCode: number; message: string; data: FeeResponse[]; additionalFees?: number | string | null }> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.GetPaymentFees,
            showLoader,
            payLoad: {
                combinations: userContractsCountryCombinations,
            },
        });
        const responseData = response?.data ?? response;
        if (responseData?.statusCode && Array.isArray(responseData?.data)) {
            return responseData;
        }
        return { statusCode: 200, message: '', data: [] };
    } catch (error: any) {
        console.error('Error fetching payment fees:', error);
        return { statusCode: error?.response?.status ?? 500, message: 'Error fetching fees', data: [] };
    }
};

/** Options for GET /payments/stripe/payment-methods (query params). */
export interface GetStripePaymentMethodsOptions {
    /** Filter by type: 'card' or 'bank_account' (Stripe may return type as us_bank_account). */
    type?: 'card' | 'bank_account' | 'us_bank_account';
    /** Max number of payment methods to return. */
    limit?: number;
}

/**
 * Fetch saved payment methods (cards / bank accounts) from Stripe for the current user.
 * GET /payments/stripe/payment-methods with optional query params: type, limit.
 */
export const getStripePaymentMethods = async (
    showLoader: boolean = false,
    options?: GetStripePaymentMethodsOptions,
): Promise<GetStripePaymentMethodsResponse> => {
    try {
        const params: Record<string, string | number | undefined> = {};
        if (options?.type) params.type = options.type;
        if (options?.limit != null) params.limit = options.limit;

        const response: any = await api_service.get({
            endPoint: api_urls.GetStripePaymentMethods,
            showLoader,
            payLoad: Object.keys(params).length ? params : undefined,
        });
        const responseData = response?.data ?? response;
        let rawList: StripePaymentMethodApiItem[] =
            responseData?.data?.paymentMethods ?? responseData?.paymentMethods ?? [];
        if (!Array.isArray(rawList) && Array.isArray(responseData?.data)) {
            rawList = responseData.data;
        }
        if (!Array.isArray(rawList)) {
            return { statusCode: 200, data: [] };
        }
        const data = rawList
            .filter((item: any) => item?.id && (item.type === 'card' || item.type === 'us_bank_account'))
            .map((item: StripePaymentMethodApiItem) => mapPaymentMethodApiToDisplay(item));
        return { statusCode: 200, data };
    } catch (error: any) {
        console.error('Error fetching Stripe payment methods:', error);
        return { statusCode: error?.response?.status ?? 500, data: [] };
    }
};

export interface CreateStripeSetupIntentResponse {
    statusCode?: number;
    clientSecret?: string;
    id?: string;
    data?: any;
    message?: string;
}

/**
 * Create a Stripe SetupIntent to save a payment method for future use.
 * POST /payments/stripe/setup-intent (no parameters). Returns 201 with clientSecret etc.
 */
export const createStripeSetupIntent = async (
    showLoader: boolean = false,
): Promise<CreateStripeSetupIntentResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.CreateStripeSetupIntent,
            payLoad: {},
            showLoader,
        });
        const data = response?.data ?? response;
        return {
            statusCode: response?.status ?? data?.statusCode ?? 201,
            clientSecret: data?.clientSecret ?? data?.data?.clientSecret,
            id: data?.id ?? data?.data?.id,
            data: data?.data ?? data,
            message: data?.message,
        };
    } catch (error: any) {
        console.error('Error creating Stripe SetupIntent:', error);
        throw error;
    }
};

/** Payload to create Stripe PaymentIntents (one per row). */
export interface CreateStripePaymentIntentsPayload {
    paymentRows: Array<{
        contractId: string;
        amount: number;
        paymentDate?: string;
    }>;
}

export interface CreateStripePaymentIntentsResponse {
    statusCode?: number;
    paymentIntents?: StripePaymentIntentItem[];
    message?: string;
}

/**
 * Create Stripe PaymentIntents for the given payment rows.
 * Backend returns an array of { id, clientSecret, amount, metadata } to confirm one-by-one.
 */
export const createStripePaymentIntents = async (
    payload: CreateStripePaymentIntentsPayload,
    showLoader: boolean = false,
): Promise<CreateStripePaymentIntentsResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.CreateStripePaymentIntents,
            payLoad: payload,
            showLoader,
        });
        const data = response?.data ?? response;
        const list = data?.paymentIntents ?? data?.data?.paymentIntents ?? data?.data;
        const paymentIntents = Array.isArray(list) ? list : [];
        return {
            statusCode: data?.statusCode ?? response?.status ?? 200,
            paymentIntents,
            message: data?.message ?? response?.message,
        };
    } catch (error: any) {
        console.error('Error creating Stripe payment intents:', error);
        throw error;
    }
};

/** Single payment item for bulk PaymentIntents request. */
export interface BulkPaymentIntentItem {
    contractId: number;
    amount: number;
    metadata: { contractId?: string; paymentId: string; [key: string]: string | undefined };
    description: string;
    paymentMethodId: string;
}

export interface CreateBulkStripePaymentIntentsPayload {
    currency: string;
    payments: BulkPaymentIntentItem[];
    idempotencyKeyPrefix: string;
}

/** One PaymentIntent returned from bulk create (for client-side confirmCardPayment). */
export interface BulkPaymentIntentResponseItem {
    id?: string;
    clientSecret: string;
    status?: string;
}

/** Per-payment validation error returned by the bulk Stripe payment-intents endpoint on a 400. */
export interface BulkPaymentValidationError {
    index?: number;
    contractId?: string | number;
    error?: string;
}

export interface CreateBulkStripePaymentIntentsResponse {
    statusCode?: number;
    data?: any;
    message?: string;
    paymentIntents?: BulkPaymentIntentResponseItem[];
    errors?: BulkPaymentValidationError[];
}

/**
 * Create bulk Stripe PaymentIntents to charge saved payment methods.
 * POST /payments/stripe/bulk-payment-intents.
 */
export const createBulkStripePaymentIntents = async (
    payload: CreateBulkStripePaymentIntentsPayload,
    showLoader: boolean = true,
): Promise<CreateBulkStripePaymentIntentsResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.CreateBulkStripePaymentIntents,
            payLoad: payload,
            showLoader,
        });
        const data = response?.data ?? response;
        const raw = data?.data ?? data;
        const list = raw?.paymentIntents ?? raw?.payment_intents ?? data?.paymentIntents ?? [];
        const paymentIntents = Array.isArray(list)
            ? list.map((item: any) => ({
                  id: item?.id,
                  clientSecret: item?.clientSecret ?? item?.client_secret ?? '',
                  status: item?.status,
              }))
            : [];
        return {
            statusCode: response?.status ?? data?.statusCode ?? 201,
            data: raw ?? data,
            message: data?.message ?? response?.message,
            paymentIntents: paymentIntents.filter((pi: BulkPaymentIntentResponseItem) => pi.clientSecret),
            errors: data?.errors ?? raw?.errors,
        };
    } catch (error: any) {
        console.error('Error creating bulk Stripe payment intents:', error);
        throw error;
    }
};

export const cancelScheduledPayment = async (paymentId: string): Promise<any> => {
    try {
        const response: any = await api_service.delete({
            endPoint: `${api_urls.CancelScheduledPayment}/${paymentId}`,
            showLoader: true,
        });
        return response?.data as any;
    } catch (error: any) {
        console.error('Error canceling scheduled payment:', error);
        throw error;
    }
};

export const enableAutoPay = async (requestPayload: any, showLoader: boolean): Promise<any> => {
    try {
        const payLoadData = {
            startDate: requestPayload?.startDate,
            bankAccountId: requestPayload?.bankAccountId,
            signature: requestPayload?.signature,
        };

        const response: any = await api_service.put({
            endPoint: `${api_urls.UpdateEnableAutoPay}/${requestPayload?.contractId}`,
            payLoad: payLoadData,
            headerType: api_service.ContentHeaders.Json,
            showLoader,
        });
        return response?.data || response;
    } catch (error: any) {
        console.error('Error enabling autopay:', error);
        throw error;
    }
};

export interface CreateBulkPaymentPayload {
    contractId: string;
    amount: number;
    paymentDate: string;
    bankAccountId: string;
}

export interface CreateBulkPaymentResponse {
    success?: boolean;
    statusCode?: number;
    message?: string;
    errors?: Array<{
        index?: number;
        contractId?: string | number;
        error?: string;
        success?: boolean;
    }>;
    data?: {
        total?: number;
        successful?: number;
        failed?: number;
        results?: Array<any>;
        errors?: Array<{
            index: number;
            success: boolean;
            contractId: string;
            error: string;
        }>;
    };
}

/**
 * Create bulk payments
 * @param payload - Array of payment payloads
 * @param showLoader - Whether to show loading indicator (default: true)
 * @returns Promise with create bulk payment response
 */
export const makeBulkPayment = async (
    payload: Array<CreateBulkPaymentPayload>,
    showLoader: boolean = true,
): Promise<CreateBulkPaymentResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.MakeBulkPayment,
            payLoad: payload,
            showLoader,
        });
        const responseData = response?.data || response;
        return responseData as CreateBulkPaymentResponse;
    } catch (error: any) {
        console.error('Error creating bulk payment:', error);
        throw error;
    }
};

/** Headers for Ello email-link flows: JWT (or similar) from the link overrides portal session auth. */
export const buildElloLinkRequestHeaders = (linkToken: string): Record<string, string> => {
    const t = String(linkToken || '').trim();
    return { Authorization: `${t}` };
};

export interface ElloPaymentDetailsApiResponse {
    statusCode?: number;
    message?: string;
    data?: unknown;
}

/** One network GET per paymentId+token: React Strict Mode / remounts often fire the effect several times. */
const elloPaymentDetailsInflight = new Map<string, Promise<ElloPaymentDetailsApiResponse>>();
const elloPaymentDetailsCache = new Map<string, { expiresAt: number; value: ElloPaymentDetailsApiResponse }>();
const ELLO_PAYMENT_DETAILS_CACHE_TTL_MS = 3 * 60 * 1000;

function elloPaymentDetailsDedupeKey(paymentId: string, linkToken: string): string {
    return `${String(paymentId).trim()}\u0001${String(linkToken).trim()}`;
}

/**
 * GET payments/elloai/details?paymentId=… — requires link token in Authorization header.
 * Deduplicates concurrent and rapid repeat calls (same paymentId + token) so only one request hits the network.
 */
export const getElloPaymentDetails = async (
    paymentId: string,
    linkToken: string,
    showLoader: boolean = true,
): Promise<ElloPaymentDetailsApiResponse> => {
    const key = elloPaymentDetailsDedupeKey(paymentId, linkToken);

    const cached = elloPaymentDetailsCache.get(key);
    if (cached && cached.expiresAt > Date.now()) {
        return cached.value;
    }

    const existing = elloPaymentDetailsInflight.get(key);
    if (existing) {
        return existing;
    }

    const promise = (async (): Promise<ElloPaymentDetailsApiResponse> => {
        try {
            const response: any = await api_service.get({
                endPoint: api_urls.ElloAiPaymentDetails,
                payLoad: { paymentId },
                customHeaders: buildElloLinkRequestHeaders(linkToken),
                showLoader,
            });
            const data = (response?.data ?? response) as ElloPaymentDetailsApiResponse;
            elloPaymentDetailsCache.set(key, {
                expiresAt: Date.now() + ELLO_PAYMENT_DETAILS_CACHE_TTL_MS,
                value: data,
            });
            return data;
        } finally {
            elloPaymentDetailsInflight.delete(key);
        }
    })();

    elloPaymentDetailsInflight.set(key, promise);
    return promise;
};

export interface ElloPaymentConfirmPayload {
    paymentId: string;
    /** 1 = approve (accept), 2 = decline */
    status: 1 | 2;
}

/** Shape returned by POST payments/elloai/confirm (may include `data` on decline). */
export interface ElloPaymentConfirmApiResponse {
    statusCode?: number;
    message?: string;
    data?: Record<string, unknown>;
}

/**
 * POST payments/elloai/confirm — requires link token in Authorization header.
 */
export const confirmElloPayment = async (
    payload: ElloPaymentConfirmPayload,
    linkToken: string,
    showLoader: boolean = true,
): Promise<ElloPaymentConfirmApiResponse> => {
    const response: any = await api_service.post({
        endPoint: api_urls.ElloAiPaymentConfirm,
        payLoad: payload,
        customHeaders: buildElloLinkRequestHeaders(linkToken),
        showLoader,
    });
    return (response?.data ?? response) as ElloPaymentConfirmApiResponse;
};
